import React, { useEffect } from 'react';
import BasicModal from '../../../components/BasicModal';
import { translate } from '../../../services/translate';
import { Tabs } from '../../../components/Tabs';
import { View } from 'react-native';
import Badge from '../../../components/Badge';
import { ScrollView } from 'react-native-web-hover';
import { SectionHeader } from '../../FormCommons';
import { useForm } from 'react-hook-form';
import FormInput from '../../../components/FormInput';
import MyCpfInput from '../../../components/MyCpfInput';
import FormFileUploader from '../../../components/FormFileUploader';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import DataTableContainer from '../../../components/DataTableContainer';
import MyAppText from '../../../components/MyAppText';
import Divider from '../../../components/Divider';
import { WindowInformation } from '../../../services/window-information';
import FontWeight from '../../../styles/fontWeights';
import MyRadio from '../../../components/MyRadio';
import MyDateInput from '../../../components/MyDateInput';

interface ProtectiveMeasureModalProps {
    isModalVisible: boolean;
    setModalVisible: (value: boolean) => void;
    protectiveMeasure?: ProtectiveMeasure;
    onSave: (value: ProtectiveMeasure) => void;
}

interface ProtectiveMeasureForm {
    name: string;
    document: string;
    occurrenceDate: string | null;
    caseNumber: string;
    prosecutorOffice: string;
    qualifiers: {
        aggressorNotified: 'yes' | 'no' | null;
        attemptedFemicide: 'yes' | 'no' | null;
        firearmUsed: 'yes' | 'no' | null;
        victimPregnant: 'yes' | 'no' | null;
        severeInjury: 'yes' | 'no' | null;
        hospitalizationDueToIncident: 'yes' | 'no' | null;
        aggressorProvisionalRelease: 'yes' | 'no' | null;
        aggressorElectronicMonitoring: 'yes' | 'no' | null;
    };
    policeReport: {
        file: File | undefined;
        fileName: string;
        fileId: number;
    } | null;
    observation: string;
    photos: {
        file: File | undefined;
        fileName: string;
        fileId: string;
    }[];
}

export default function ProtectiveMeasureModal(props: ProtectiveMeasureModalProps) {

    const { styles, theme } = useStyles(stylesheet);

    const windowInfo = WindowInformation();

    const defaultEmptyValues: ProtectiveMeasureForm = {
        name: '',
        document: '',
        observation: '',
        caseNumber: '',
        prosecutorOffice: '',
        occurrenceDate: null,
        photos: [],
        policeReport: null,
        qualifiers: {
            aggressorNotified: null,
            attemptedFemicide: null,
            firearmUsed: null,
            victimPregnant: null,
            severeInjury: null,
            hospitalizationDueToIncident: null,
            aggressorProvisionalRelease: null,
            aggressorElectronicMonitoring: null,
        }
    };

    useEffect(() => {
        if (!props.protectiveMeasure) {
            form.reset(defaultEmptyValues);
        } else {
            form.reset({
                ...defaultEmptyValues,
                name: props.protectiveMeasure.name,
                document: props.protectiveMeasure.document,
                observation: props.protectiveMeasure.observation || '',
                caseNumber: props.protectiveMeasure.caseNumber,
                occurrenceDate: props.protectiveMeasure.occurrenceDate,
                photos: [],
                prosecutorOffice: props.protectiveMeasure.prosecutorOffice,
                policeReport: null,
                qualifiers: {
                    aggressorNotified: props.protectiveMeasure.aggressorNotified === null ? null : props.protectiveMeasure.aggressorNotified ? 'yes' : 'no',
                    attemptedFemicide: props.protectiveMeasure.attemptedFemicide === null ? null : props.protectiveMeasure.attemptedFemicide ? 'yes' : 'no',
                    firearmUsed: props.protectiveMeasure.firearmUsed === null ? null : props.protectiveMeasure.firearmUsed ? 'yes' : 'no',
                    victimPregnant: props.protectiveMeasure.victimPregnant === null ? null : props.protectiveMeasure.victimPregnant ? 'yes' : 'no',
                    severeInjury: props.protectiveMeasure.severeInjury === null ? null : props.protectiveMeasure.severeInjury ? 'yes' : 'no',
                    hospitalizationDueToIncident: props.protectiveMeasure.hospitalizationDueToIncident === null ? null : props.protectiveMeasure.hospitalizationDueToIncident ? 'yes' : 'no',
                    aggressorProvisionalRelease: props.protectiveMeasure.aggressorProvisionalRelease === null ? null : props.protectiveMeasure.aggressorProvisionalRelease ? 'yes' : 'no',
                    aggressorElectronicMonitoring: props.protectiveMeasure.aggressorElectronicMonitoring === null ? null : props.protectiveMeasure.aggressorElectronicMonitoring ? 'yes' : 'no',
                }
            });
        }
    }, [props.protectiveMeasure]);

    useEffect(() => {
        if (!props.isModalVisible) {
            form.reset(defaultEmptyValues);
        }
    }, [props.isModalVisible]);

    const form = useForm<ProtectiveMeasureForm>({
        defaultValues: defaultEmptyValues,
        mode: 'onBlur',
    });

    function castYesNoToBoolean(value: 'yes' | 'no' | null) {
        if (value === 'yes') {
            return true;
        }
        if (value === 'no') {
            return false;
        }
        return null;
    }

    return <BasicModal
        isModalVisible={props.isModalVisible}
        setModalVisible={props.setModalVisible}
        title={translate('protectiveMeasureRegister')}
        width='extra-large'
        primaryButton={props.protectiveMeasure?.integrationId || props.protectiveMeasure?.deletedAt ? undefined : {
            text: translate('save'),
            disabled: !form.formState.isValid,
            onClick: () => {
                const values = form.getValues();
                props.onSave({
                    id: props.protectiveMeasure?.id,
                    aggressorElectronicMonitoring: castYesNoToBoolean(values.qualifiers.aggressorElectronicMonitoring),
                    aggressorNotified: castYesNoToBoolean(values.qualifiers.aggressorNotified),
                    aggressorProvisionalRelease: castYesNoToBoolean(values.qualifiers.aggressorProvisionalRelease),
                    attemptedFemicide: castYesNoToBoolean(values.qualifiers.attemptedFemicide),
                    blobStorageId: null,
                    caseNumber: values.caseNumber,
                    createdAt: props.protectiveMeasure?.createdAt || new Date().toISOString(),
                    updatedAt: props.protectiveMeasure?.updatedAt || new Date().toISOString(),
                    deletedAt: null,
                    document: values.document,
                    firearmUsed: castYesNoToBoolean(values.qualifiers.firearmUsed),
                    guardianWomanId: -1,
                    hospitalizationDueToIncident: castYesNoToBoolean(values.qualifiers.hospitalizationDueToIncident),
                    integrationId: null,
                    name: values.name,
                    observation: values.observation,
                    occurrenceDate: values.occurrenceDate,
                    prosecutorOffice: values.prosecutorOffice,
                    severeInjury: castYesNoToBoolean(values.qualifiers.severeInjury),
                    victimPregnant: castYesNoToBoolean(values.qualifiers.victimPregnant),
                    /**
                     * TODO: implement these image uploads
                     */
                    policeReportUrl: null,
                    aggressorImages: [],
                });
            }
        }}
        cancelButton={{
            customText: translate(props.protectiveMeasure?.integrationId || props.protectiveMeasure?.deletedAt ? 'close' : 'cancel'),
        }}
        disableScrollView
    >
        <View style={{ height: 645 }}>
            <Tabs
                tabs={[{
                    key: 'general',
                    label: translate('aggressorInformation'),
                }, {
                    key: 'occurrences',
                    label: translate('occurrenceData'),
                }, {
                    key: 'qualifications',
                    label: translate('qualifications'),
                }]}
                endContent={
                    <View style={[{ justifyContent: 'center', alignSelf: 'flex-end' }]}>
                        {props.protectiveMeasure?.integrationId && <Badge
                            mode='SQUARE'
                            color={'teal'}
                        >{translate('integration')}</Badge>}
                    </View>
                }
            >
                {({ selectedTab }) => {
                    if (selectedTab === 'general') {
                        return <ScrollView style={{ flex: 1 }} contentContainerStyle={{ gap: 16 }}>
                            <SectionHeader title='generalData' viewZIndex={3}>
                                <View style={styles.row}>
                                    <View style={windowInfo.isMobile ? { width: '100%' } : { flex: 4 }}>
                                        <FormInput
                                            form={form}
                                            fieldName={'name'}
                                            disabled={Boolean(props.protectiveMeasure?.integrationId || props.protectiveMeasure?.deletedAt)}
                                            label={translate('aggressorName')}
                                            required
                                        />
                                    </View>
                                    <View style={windowInfo.isMobile ? { width: '100%' } : { flex: 1 }}>
                                        <MyCpfInput
                                            fieldName='document'
                                            disabled={Boolean(props.protectiveMeasure?.integrationId || props.protectiveMeasure?.deletedAt)}
                                            form={form}
                                            viewStyle={{ flex: 1 }}
                                        />
                                    </View>
                                </View>
                            </SectionHeader>
                            <SectionHeader title='aggressorImages' viewZIndex={2}>
                                <FormFileUploader
                                    form={form}
                                    fieldName='photos'
                                    disabled={Boolean(props.protectiveMeasure?.integrationId || props.protectiveMeasure?.deletedAt)}
                                    multiple
                                    // allowDownload={IsEditing && form.watch('status') != 'failed'}
                                    // disabled={inViewMode}
                                    uploadComponentStyle={form.watch('photos')?.length > 0 ? {
                                        position: 'sticky',
                                        zIndex: 10,
                                        width: '98%',
                                        top: 0,
                                        backgroundColor: theme.colors.white['500']
                                    } : {}}
                                    supportedFormatsText={translate('supported', { fileTypes: ['.jpg', '.jpeg', '.png'].join(', ') })}
                                    allowedFileTypes={['image/jpg', 'image/png', 'image/jpeg']}
                                />
                            </SectionHeader>
                        </ScrollView>;
                    }
                    if (selectedTab === 'occurrences') {
                        return <ScrollView style={{ flex: 1 }} contentContainerStyle={{ gap: 16 }}>
                            <SectionHeader title='occurrenceData' viewZIndex={3}>
                                <View style={[styles.row]}>
                                    <View style={[windowInfo.isMobile ? { width: '100%' } : { flex: 1 }]}>

                                        <MyDateInput
                                            form={form}
                                            fieldName='occurrenceDate'
                                            label={translate('occurrenceDate')}
                                            editable={!props.protectiveMeasure?.integrationId && !props.protectiveMeasure?.deletedAt}
                                            allowDateBiggerThanToday={false}
                                        />
                                    </View>
                                    <View style={windowInfo.isMobile ? { width: '100%' } : { flex: 4 }}>
                                        <FormInput
                                            form={form}
                                            fieldName='caseNumber'
                                            disabled={Boolean(props.protectiveMeasure?.integrationId || props.protectiveMeasure?.deletedAt)}
                                            label={translate('caseNumber')}
                                        />
                                    </View>
                                </View>
                                <FormFileUploader
                                    form={form}
                                    fieldName={'policeReport'}
                                    disabled={Boolean(props.protectiveMeasure?.integrationId || props.protectiveMeasure?.deletedAt)}
                                    label={translate('policeReport')}
                                    supportedFormatsText={translate('supported', { fileTypes: ['.jpg', '.jpeg', '.png', '.pdf'].join(', ') })}
                                    maxFileSize={10}
                                    allowedFileTypes={['image/jpg', 'image/png', 'image/jpeg', 'application/pdf']}
                                    // actions={getFileActions(item as keyof ApprovedCompaniesFiles)}
                                    defaultValues={{
                                        file: undefined,
                                        fileName: '',
                                    }}
                                    emptyObjectValues={{
                                        file: undefined,
                                        fileName: '',
                                    }}
                                    previewStyle={{ flexDirection: windowInfo.isMobile ? 'column' : 'row' }}
                                // allowDownload={IsEditing && form.watch('status') != 'failed'}
                                // disabled={inViewMode}
                                />
                            </SectionHeader>
                            <SectionHeader title='otherInformation' viewZIndex={2}>
                                <FormInput
                                    form={form}
                                    fieldName='prosecutorOffice'
                                    disabled={Boolean(props.protectiveMeasure?.integrationId || props.protectiveMeasure?.deletedAt)}
                                    label={translate('prosecutorOffice')}
                                />
                                <FormInput
                                    form={form}
                                    fieldName='observation'
                                    disabled={Boolean(props.protectiveMeasure?.integrationId || props.protectiveMeasure?.deletedAt)}
                                    label={translate('observations')}
                                    maxCharacters={255}
                                    numberOfLines={3}
                                    multiline
                                />
                            </SectionHeader>
                        </ScrollView>;
                    }
                    if (selectedTab === 'qualifications') {
                        return <ScrollView style={{ flex: 1 }} contentContainerStyle={{ gap: 16 }}>
                            <DataTableContainer
                                dataTable={<>
                                    <View style={{
                                        height: 40,
                                        backgroundColor: theme.colors.backgrounds['BG-2'],
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}>
                                        <MyAppText style={{
                                            fontWeight: FontWeight.semiBold,
                                            fontSize: 12,
                                        }}>{translate('occurrenceInformation')}</MyAppText>
                                    </View>
                                    {[
                                        'aggressorNotified',
                                        'attemptedFemicide',
                                        'firearmUsed',
                                        'victimPregnant',
                                        'severeInjury',
                                        'hospitalizationDueToIncident',
                                        'aggressorProvisionalRelease',
                                        'aggressorElectronicMonitoring',
                                    ].map(qualifier => <>
                                        <Divider />
                                        <View style={{
                                            height: 54,
                                            alignItems: 'center',
                                            paddingHorizontal: 16,
                                            gap: 40,
                                            flexDirection: 'row',
                                        }}>
                                            <MyRadio
                                                editable={!props.protectiveMeasure?.integrationId && !props.protectiveMeasure?.deletedAt}
                                                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                                selected={form.watch(`qualifiers.${qualifier}` as any)}
                                                setSelected={(value: 'yes' | 'no' | null) => {
                                                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                                    form.setValue(`qualifiers.${qualifier}` as any, value);
                                                }}
                                                options={[
                                                    { label: translate('yes'), value: 'yes' },
                                                    { label: translate('no'), value: 'no' },
                                                    { label: translate('noInformation'), value: null },
                                                ]}
                                                labelFontSize={14}
                                            />
                                            <MyAppText style={{
                                                color: theme.colors.neutral[400],
                                                fontSize: 14,
                                            }}>{translate(qualifier)}</MyAppText>
                                        </View>
                                    </>
                                    )}
                                </>}
                            />
                        </ScrollView>;
                    }
                }}
            </Tabs>
        </View>
    </BasicModal>;
}

const stylesheet = createStyleSheet(() => ({
    row: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        flexWrap: 'wrap',
        columnGap: 30,
        minHeight: 'auto',
        rowGap: 20,
    }
}));
